import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";

const CONF_OPTIONS = ["LIRSS", "LIBSS", "Both"];

const emptyRow = { name: "", country: "", conference: "LIRSS", bio: "", order: 0 };

const FacultyEditor = () => {
  const [members, setMembers] = useState([]);
  const [newRow, setNewRow] = useState(emptyRow);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } });

  const load = async () => {
    setLoading(true);
    const res = await axios.get(`${API}/faculty/`, authHeaders());
    setMembers(res.data.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleFieldChange = (id, field, value) => {
    setMembers(prev => prev.map(m => m._id === id ? { ...m, [field]: value } : m));
  };

  const handleSave = async (row) => {
    const { _id, ...rest } = row;
    await axios.put(`${API}/faculty/${_id}`, rest, authHeaders());
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this faculty member?")) return;
    await axios.delete(`${API}/faculty/${id}`, authHeaders());
    load();
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/faculty/`, newRow, authHeaders());
    setNewRow(emptyRow);
    load();
  };

  if (loading) return <div className="text-slate-400 p-4">Loading faculty...</div>;

  return (
    <div className="bg-slate-800/60 rounded-xl border border-slate-700 p-6" data-testid="faculty-editor">
      <h2 className="text-lg font-semibold text-white mb-1">Faculty Directory ({members.length} members)</h2>
      <p className="text-slate-400 text-xs mb-4">
        Conference tag controls which delegates see each member on their Hub.
        <span className="text-sky-400"> LIRSS</span> or
        <span className="text-amber-400"> LIBSS</span> → single-conf delegates only.
        <span className="text-emerald-400"> Both</span> → visible to all delegates.
      </p>

      <div className="space-y-2" data-testid="faculty-list">
        {members.map((row) => (
          <div key={row._id} className="border border-slate-700 rounded-lg bg-slate-800/40" data-testid={`faculty-row-${row._id}`}>
            <div className="flex items-center gap-3 px-3 py-2">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                row.conference === "LIRSS" ? "bg-sky-500/20 text-sky-400" :
                row.conference === "LIBSS" ? "bg-amber-500/20 text-amber-400" :
                "bg-emerald-500/20 text-emerald-400"
              }`}>{row.conference}</span>
              <span className="text-white text-sm font-medium flex-1">{row.name}</span>
              <span className="text-slate-400 text-xs">{row.country}</span>
              <button
                onClick={() => setExpandedId(expandedId === row._id ? null : row._id)}
                className="text-slate-400 hover:text-white text-xs ml-2"
                data-testid={`faculty-expand-${row._id}`}
              >
                {expandedId === row._id ? "Collapse" : "Edit"}
              </button>
              <button
                onClick={() => handleDelete(row._id)}
                className="text-red-400 hover:text-red-300 text-xs"
                data-testid={`faculty-delete-${row._id}`}
              >
                Delete
              </button>
            </div>

            {expandedId === row._id && (
              <div className="px-3 pb-3 pt-1 border-t border-slate-700 space-y-2">
                <div className="flex flex-wrap gap-2">
                  <input
                    value={row.name}
                    onChange={(e) => handleFieldChange(row._id, "name", e.target.value)}
                    placeholder="Name"
                    className="bg-slate-700 rounded px-2 py-1 text-white text-xs w-48"
                    data-testid={`faculty-name-${row._id}`}
                  />
                  <input
                    value={row.country}
                    onChange={(e) => handleFieldChange(row._id, "country", e.target.value)}
                    placeholder="Country"
                    className="bg-slate-700 rounded px-2 py-1 text-white text-xs w-32"
                    data-testid={`faculty-country-${row._id}`}
                  />
                  <select
                    value={row.conference}
                    onChange={(e) => handleFieldChange(row._id, "conference", e.target.value)}
                    className="bg-slate-700 rounded px-2 py-1 text-white text-xs"
                    data-testid={`faculty-conference-${row._id}`}
                  >
                    {CONF_OPTIONS.map(c => <option key={c}>{c}</option>)}
                  </select>
                  <input
                    type="number"
                    value={row.order}
                    onChange={(e) => handleFieldChange(row._id, "order", parseInt(e.target.value) || 0)}
                    placeholder="Order"
                    className="bg-slate-700 rounded px-2 py-1 text-white text-xs w-16"
                  />
                </div>
                <textarea
                  value={row.bio}
                  onChange={(e) => handleFieldChange(row._id, "bio", e.target.value)}
                  placeholder="Bio"
                  rows={3}
                  className="w-full bg-slate-700 rounded px-2 py-1 text-white text-xs"
                  data-testid={`faculty-bio-${row._id}`}
                />
                <button
                  onClick={() => handleSave(row)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1.5 rounded"
                  data-testid={`faculty-save-${row._id}`}
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add new */}
      <form onSubmit={handleAdd} className="mt-6 border-t border-slate-700 pt-4 space-y-2" data-testid="faculty-add-form">
        <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide">Add New Faculty Member</p>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            required
            placeholder="Full Name"
            value={newRow.name}
            onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
            className="bg-slate-700 rounded px-2 py-1 text-white text-xs w-48"
            data-testid="faculty-new-name"
          />
          <input
            required
            placeholder="Country"
            value={newRow.country}
            onChange={(e) => setNewRow({ ...newRow, country: e.target.value })}
            className="bg-slate-700 rounded px-2 py-1 text-white text-xs w-32"
            data-testid="faculty-new-country"
          />
          <select
            value={newRow.conference}
            onChange={(e) => setNewRow({ ...newRow, conference: e.target.value })}
            className="bg-slate-700 rounded px-2 py-1 text-white text-xs"
            data-testid="faculty-new-conference"
          >
            {CONF_OPTIONS.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <textarea
          required
          placeholder="Bio"
          value={newRow.bio}
          onChange={(e) => setNewRow({ ...newRow, bio: e.target.value })}
          rows={2}
          className="w-full bg-slate-700 rounded px-2 py-1 text-white text-xs"
          data-testid="faculty-new-bio"
        />
        <button
          type="submit"
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3 py-1.5 rounded"
          data-testid="faculty-add-btn"
        >
          Add Faculty Member
        </button>
      </form>
    </div>
  );
};

export default FacultyEditor;
